import { z } from 'zod';
/**
 * Контракты модуля Hermes — внешней оценки трудоёмкости.
 * docs/modules/hermes-estimate.md. Внешний контракт — docs/external/hermes-api.md.
 *
 * Поток обратный Jira-sync: запись Lens → task_description → асинхронная
 * оценка (polling) → запись result обратно в поля Lens. Ключ X-API-Key
 * хранится per-space (M2M), наружу не отдаётся.
 */
// ─── Режим оценки ──────────────────────────────────────────────────────────
export const HERMES_ESTIMATION_MODES = ['high_level', 'detailed', 'auto'];
export const HermesEstimationModeSchema = z.enum(HERMES_ESTIMATION_MODES);
// ─── Сценарий оценки ───────────────────────────────────────────────────────
// Какой из трёх итогов Hermes (и разбивку by_role_spread) записывать в поля Lens.
export const HERMES_SCENARIOS = ['realistic', 'optimistic', 'pessimistic'];
export const HermesScenarioSchema = z.enum(HERMES_SCENARIOS);
// ─── Маппинг входа: что станет task_description ─────────────────────────────
export const HermesInputMappingSchema = z.object({
    /** slug text/textarea/markdown поля — обязательно. */
    taskDescriptionSource: z.string().min(1),
    /** Доп. поля, добавляемые строками "Имя поля: значение". */
    contextFieldSlugs: z.array(z.string().min(1)).optional(),
});
// ─── Пути внутри result (фиксированный enum) ───────────────────────────────
export const HERMES_RESULT_PATHS = [
    'title',
    'description',
    'summary',
    'task_class',
    'estimate.total.realistic',
    'estimate.total.optimistic',
    'estimate.total.pessimistic',
    'estimate.by_role.backend',
    'estimate.by_role.frontend',
    'estimate.by_role.analytics',
    'estimate.by_role.testing',
    'estimate.by_role.design',
    'estimate.unit',
    'solution.title',
    'solution.description',
    'solution.components',
    'solution.steps',
    'solution.excluded',
    'solution.risks',
    'risks',
];
// Роль, которой нет в фиксированном списке (экземпляр Hermes может вернуть
// любую — devops, архитектура и т.д.), задаётся строкой 'estimate.by_role.<role>'.
export const HERMES_BY_ROLE_PREFIX = 'estimate.by_role.';
export const HermesResultPathSchema = z.union([
    z.enum(HERMES_RESULT_PATHS),
    z.string().regex(/^estimate\.by_role\.[a-zA-Z0-9_-]+$/, 'Ожидался путь estimate.by_role.<роль>'),
]);
export const HERMES_RESULT_TRANSFORMS = ['number', 'text', 'join_list', 'json'];
export const HermesResultTransformSchema = z.enum(HERMES_RESULT_TRANSFORMS);
// ─── Маппинг результата: путь в result → поле Lens ─────────────────────────
export const HermesResultMappingEntrySchema = z.object({
    resultPath: HermesResultPathSchema,
    activitiesFieldSlug: z.string().min(1),
    transform: HermesResultTransformSchema,
});
export const HermesResultMappingSchema = z.object({
    fields: z.array(HermesResultMappingEntrySchema),
});
// ─── Поведение при отсутствующем поле result ───────────────────────────────
export const HERMES_ON_MISSING_PATH = ['skip', 'clear'];
export const HermesOnMissingPathSchema = z.enum(HERMES_ON_MISSING_PATH);
export const HermesConfigSettingsSchema = z.object({
    estimationMode: HermesEstimationModeSchema.default('auto'),
    onMissingPath: HermesOnMissingPathSchema.default('skip'),
    /** Фаза B: автозапуск оценки при создании записи. */
    autoEstimateOnCreate: z.boolean().optional(),
});
// ─── Ответ Hermes (result) ─────────────────────────────────────────────────
// API §3: ЛЮБОЕ поле внутри result может отсутствовать (агент отключён в
// экземпляре) — поэтому всё опционально, и числа могут прийти округлёнными.
export const HermesEstimateBreakdownSchema = z
    .object({
    total: z
        .object({
        realistic: z.number().optional(),
        optimistic: z.number().optional(),
        pessimistic: z.number().optional(),
    })
        .partial()
        .optional(),
    by_role: z.record(z.string(), z.number()).optional(),
    // Разбивка по ролям на каждый сценарий (ITEM-12 «полная оценка»). Опционально:
    // старые результаты и экземпляры без агента-раскладки его не присылают.
    by_role_spread: z
        .record(z.string(), z
        .object({
        realistic: z.number().optional(),
        optimistic: z.number().optional(),
        pessimistic: z.number().optional(),
    })
        .partial())
        .optional(),
    unit: z.string().optional(),
})
    .partial();
export const HermesSolutionSchema = z
    .object({
    title: z.string().optional(),
    description: z.string().optional(),
    components: z.array(z.string()).optional(),
    steps: z.array(z.string()).optional(),
    excluded: z.array(z.string()).optional(),
    risks: z.array(z.string()).optional(),
})
    .partial();
export const HermesRiskSchema = z.object({
    severity: z.string().optional(),
    description: z.string().optional(),
});
export const HermesResultSchema = z
    .object({
    title: z.string().optional(),
    description: z.string().optional(),
    task_class: z.string().optional(),
    estimate: HermesEstimateBreakdownSchema.optional(),
    solution: HermesSolutionSchema.optional(),
    risks: z.array(HermesRiskSchema).optional(),
    summary: z.string().optional(),
})
    .passthrough();
export const HERMES_TASK_STATUSES = ['pending', 'running', 'completed', 'failed'];
export const HermesTaskStatusSchema = z.enum(HERMES_TASK_STATUSES);
// ─── Стенд Hermes ──────────────────────────────────────────────────────────
// Именованная пара «адрес + ключ», общая на всю систему. Заводит админ
// экземпляра; пространства только выбирают стенд из списка.
/** То, что видит админ пространства: без ключа и без возможности править. */
export const HermesStandOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Адрес с учётом фолбэка на HERMES_BASE_URL. */
    baseUrl: z.string().nullable(),
    isDefault: z.boolean(),
});
/** Полный DTO для админки экземпляра. Ключ наружу не отдаётся (H-A2). */
export const HermesStandSchema = HermesStandOptionSchema.extend({
    /** Адрес, как он сохранён (null → берётся HERMES_BASE_URL). */
    baseUrlRaw: z.string().nullable(),
    apiKeyConfigured: z.boolean(),
    /** Сколько настроек оценки на него ссылается. */
    usedByConfigs: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateHermesStandRequestSchema = z.object({
    name: z.string().min(1).max(120),
    baseUrl: z.string().url().nullable().optional(),
    apiKey: z.string().min(1),
    isDefault: z.boolean().optional(),
});
export const UpdateHermesStandRequestSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    baseUrl: z.string().url().nullable().optional(),
    /** Пусто/нет — ключ не меняется. */
    apiKey: z.string().min(1).optional(),
    isDefault: z.boolean().optional(),
});
// ─── Config DTO (наружу — БЕЗ ключа) ───────────────────────────────────────
export const HermesEstimateConfigSchema = z.object({
    spaceId: z.string().uuid(),
    itemTypeId: z.string().uuid(),
    /** Выбранный стенд. null — настройка ещё с прежней схемы (свой адрес/ключ). */
    standId: z.string().uuid().nullable(),
    standName: z.string().nullable(),
    baseUrl: z.string().url().nullable(),
    /** Признак, что ключ задан. Сам ключ наружу не отдаётся (H-A2). */
    apiKeyConfigured: z.boolean(),
    inputMapping: HermesInputMappingSchema,
    resultMapping: HermesResultMappingSchema,
    settings: HermesConfigSettingsSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ─── PUT /api/spaces/:id/hermes-configs/:itemTypeId (upsert) ────────────────
export const UpsertHermesConfigRequestSchema = z.object({
    /** Выбранный стенд. Если задан — baseUrl/apiKey в запросе не нужны. */
    standId: z.string().uuid().nullable().optional(),
    baseUrl: z.string().url().nullable().optional(),
    /** Задать/перезаписать ключ. Пустая строка/отсутствие — не менять. */
    apiKey: z.string().min(1).optional(),
    inputMapping: HermesInputMappingSchema,
    resultMapping: HermesResultMappingSchema,
    settings: HermesConfigSettingsSchema.partial().optional(),
});
// ─── POST .../test-key ──────────────────────────────────────────────────────
/**
 * Что проверять. Приоритет: введённый вручную ключ → выбранный в форме стенд →
 * сохранённая настройка. Без `standId` проверка била бы в сохранённый стенд и
 * молчала о том, что в форме выбран другой.
 */
export const TestHermesKeyRequestSchema = z.object({
    standId: z.string().uuid().optional(),
    apiKey: z.string().min(1).optional(),
    baseUrl: z.string().url().optional(),
});
export const TestHermesKeyResponseSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), taskStatus: HermesTaskStatusSchema }),
    z.object({ ok: z.literal(false), code: z.string(), message: z.string() }),
]);
// ─── POST .../items/:itemId/hermes-estimate → { jobId } ────────────────────
// Режим оценки выбирается ПРИ ЗАПУСКЕ (не в конфиге).
export const StartHermesEstimateRequestSchema = z.object({
    estimationMode: HermesEstimationModeSchema.optional(),
});
export const HermesEstimateResponseSchema = z.object({ jobId: z.string().uuid() });
// ─── POST .../items/:itemId/hermes-apply-scenario ──────────────────────────
// Перезаписывает поля Lens значениями выбранного сценария из последней
// завершённой оценки (total.<scenario> и by_role_spread[role][scenario]).
export const ApplyHermesScenarioRequestSchema = z.object({ scenario: HermesScenarioSchema });
export const ApplyHermesScenarioResponseSchema = z.object({
    scenario: HermesScenarioSchema,
    writtenFields: z.array(z.string()),
});
// ─── POST .../items/bulk-hermes-estimate ───────────────────────────────────
export const BulkHermesEstimateRequestSchema = z.union([
    z.object({ itemIds: z.array(z.string().uuid()).min(1), estimationMode: HermesEstimationModeSchema.optional() }),
    z.object({ viewId: z.string().uuid(), estimationMode: HermesEstimationModeSchema.optional() }),
]);
// ─── GET .../items/:itemId/hermes-result ───────────────────────────────────
export const HermesItemResultSchema = z.object({
    status: z.string().nullable(),
    taskId: z.string().nullable(),
    estimatedAt: z.string().nullable(),
    result: HermesResultSchema.nullable(),
});
// ─── Источник оценки ───────────────────────────────────────────────────────
// Откуда пришёл результат: кнопка «Оценить» в Lens (api), диалог в iframe
// (embed) или внешний агент через MCP (mcp) — оценил у себя и приложил к записи.
export const HERMES_ESTIMATE_SOURCES = ['api', 'embed', 'mcp'];
export const HermesEstimateSourceSchema = z.enum(HERMES_ESTIMATE_SOURCES);
// Что именно оценивали — сохраняется вместе с результатом, чтобы оценку можно
// было объяснить и повторить. Заполняется вызывающей стороной (агентом).
export const HermesEstimateContextSchema = z.object({
    /** Текст, который реально ушёл в Гермес. */
    taskDescription: z.string().optional(),
    /** Режим оценки, в котором считали. */
    estimationMode: HermesEstimationModeSchema.optional(),
    /** Ответы на уточняющие вопросы суб-агентов: вопрос → ответ. */
    answers: z.array(z.object({ question: z.string(), answer: z.string() })).optional(),
    /** Источники контекста: страницы Confluence, файлы, ссылки. */
    sources: z.array(z.string()).optional(),
    /** Свободная заметка оценивавшего. */
    note: z.string().optional(),
});
// ─── История оценок записи ────────────────────────────────────────────────
export const HermesHistoryEntrySchema = z.object({
    id: z.string().uuid(),
    status: z.string(),
    requestedAt: z.string(),
    taskId: z.string().nullable(),
    result: HermesResultSchema.nullable(),
    /** Откуда пришла оценка; null — старые записи до появления признака. */
    source: HermesEstimateSourceSchema.nullable(),
    /** Кто запустил оценку (полное имя), null — пользователь удалён. */
    requestedBy: z.string().nullable(),
    /** Что оценивали (см. HermesEstimateContext). */
    context: HermesEstimateContextSchema.nullable(),
    /** Поля записи, в которые лёг результат. */
    writtenFields: z.array(z.string()),
    /** Поля, пропущенные из-за ручной правки после прошлой оценки. */
    skippedManualFields: z.array(z.string()),
});
// ─── Разговор оценки в Гермесе ────────────────────────────────────────────
// GET .../items/:itemId/hermes-estimates/:logId/dialog — вопросы суб-агентов и
// ответы человека из окна оценки. Сам разговор хранит Гермес; Lens читает его
// по task_id и раскладывает ответы в пары «вопрос → ответ».
export const HermesDialogRoundSchema = z.object({
    /** Суб-агент, который спрашивал (clarify_scope, fill_checklist, ...). */
    agentName: z.string().nullable(),
    /** Текст вопроса, как его показал Гермес. */
    question: z.string(),
    /** Ответ человека, разобранный по вопросам; пусто — ответа не было. */
    answers: z.array(z.object({ question: z.string(), answer: z.string() })),
    askedAt: z.string().nullable(),
});
export const HermesDialogSchema = z.object({
    taskId: z.string(),
    /** Первое сообщение разговора — описание задачи, ушедшее в Гермес. */
    taskDescription: z.string().nullable(),
    rounds: z.array(HermesDialogRoundSchema),
});
// ─── Лог попыток ────────────────────────────────────────────────────────────
export const HermesEstimateLogEntrySchema = z.object({
    id: z.string().uuid(),
    itemId: z.string().uuid().nullable(),
    externalTaskId: z.string().nullable(),
    status: z.string(),
    requestedAt: z.string(),
    // details содержит полный request (и createResponse); resultRaw — полный ответ.
    details: z.unknown(),
    resultRaw: z.unknown().nullable(),
});
// ─── Интерактивная оценка через iframe (/embed/chat) ───────────────────────
// Второй режим поверх API-режима: диалог идёт во встроенном iframe Гермеса,
// наружу — task_id + результат. Бэк минтит embed_token (X-API-Key на бэке),
// строит task_description из inputMapping и отдаёт фронту origin для iframe и
// проверки event.origin. Финализация всегда перечитывает результат с бэка.
export const HERMES_EMBED_THEMES = ['light', 'dark'];
export const HermesEmbedThemeSchema = z.enum(HERMES_EMBED_THEMES);
// POST .../items/:itemId/hermes-embed-session — режим выбирается при запуске.
export const HermesEmbedSessionRequestSchema = z.object({
    estimationMode: HermesEstimationModeSchema.optional(),
});
export const HermesEmbedSessionResponseSchema = z.object({
    /** Короткоживущий токен (только он уходит в браузер, не X-API-Key). */
    embedToken: z.string(),
    /** TTL токена, сек. */
    expiresIn: z.number(),
    /** Origin Гермеса — для проверки event.origin в обработчике message. */
    hermesOrigin: z.string().url(),
    /** Полный URL iframe (без токена) — фронт добавит ?embed=<token>. */
    embedUrl: z.string().url(),
    /** Готовый текст задачи (собран из inputMapping) — фронт шлёт в hermes:context. */
    taskDescription: z.string(),
    /** Итоговый режим (из запроса или дефолта конфига). */
    mode: HermesEstimationModeSchema,
});
// GET .../items/:itemId/hermes-presets — список пресетов оценки (discovery).
// Пресет — отдельная от mode ось: уровень коммуникации агентов и т.п. Приходит
// из внешнего эндпоинта Hermes GET /api/v1/external/presets; если экземпляр его
// не отдаёт (404) — бэк возвращает пустой список, а фронт прячет селектор.
export const HermesEmbedPresetSchema = z.object({
    /** id пресета — уходит обратно в hermes:context.preset. */
    id: z.string(),
    /** Отображаемое имя. */
    name: z.string(),
    /** Пояснение (может отсутствовать). */
    description: z.string().nullable(),
    /** Предвыбранный по умолчанию. */
    isDefault: z.boolean(),
});
export const HermesEmbedPresetsResponseSchema = z.array(HermesEmbedPresetSchema);
// POST .../items/:itemId/hermes-embed-finalize — забрать и записать результат.
export const HermesEmbedFinalizeRequestSchema = z.object({
    taskId: z.string().min(1),
});
// Ответ финализации — тот же DTO, что GET .../hermes-result.
export const HermesEmbedFinalizeResponseSchema = HermesItemResultSchema;
// ─── POST .../items/:itemId/hermes-attach-estimate ─────────────────────────
// Приложить к записи оценку, посчитанную вне Lens (агент в MCP Гермеса).
// Либо taskId — тогда Lens сам перечитает результат из Гермеса своим ключом,
// либо готовый result — тогда пишем присланное как есть (источник — агент).
export const HermesAttachEstimateRequestSchema = z
    .object({
    taskId: z.string().min(1).optional(),
    result: HermesResultSchema.optional(),
    context: HermesEstimateContextSchema.optional(),
    /** Перезаписать поля, поправленные руками после прошлой оценки. */
    overwriteManual: z.boolean().optional(),
})
    .refine((v) => Boolean(v.taskId || v.result), {
    message: 'Нужен taskId (Lens перечитает результат сам) или готовый result',
});
export const HermesAttachEstimateResponseSchema = HermesItemResultSchema.extend({
    writtenFields: z.array(z.string()),
    skippedManualFields: z.array(z.string()),
});
// ─── Перенос настроек оценки из другого пространства ───────────────────────
// Маппинги полей набиваются руками и часто повторяются. Источник — любая
// настройка Hermes в пространстве, где пользователь админ.
// GET /api/hermes-configs/sources — что можно скопировать.
export const HermesConfigSourceSchema = z.object({
    spaceId: z.string().uuid(),
    spaceName: z.string(),
    itemTypeId: z.string().uuid(),
    itemTypeName: z.string(),
    standName: z.string().nullable(),
    updatedAt: z.string(),
});
// POST .../hermes-configs/:itemTypeId/copy-from — НЕ сохраняет, а возвращает
// маппинги, пригодные для текущего типа записи: поля, которых в нём нет,
// выкидываются и перечисляются в skipped, чтобы админ это увидел.
export const CopyHermesConfigRequestSchema = z.object({
    sourceSpaceId: z.string().uuid(),
    sourceItemTypeId: z.string().uuid(),
});
export const CopyHermesConfigResponseSchema = z.object({
    inputMapping: HermesInputMappingSchema,
    resultMapping: HermesResultMappingSchema,
    settings: HermesConfigSettingsSchema,
    standId: z.string().uuid().nullable(),
    /** Слаги полей источника, которых нет в текущем типе записи. */
    skipped: z.array(z.string()),
});
